import React from 'react';
import useMyOrganizationInvitations from '../../../hooks/api/useMyOrganizationInvitations';
import { useResetApp } from '../../ResetAppProvider';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../utils/intl';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import Text from '../../v2BaseComponents/Text';
import { APITypes } from '../../../primary-record-types';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import getName from '../../../utils/getName';
import { organizationRoles } from '../../../utils/sharedMessages';
import LabelValueDisplay from '../../v2BaseComponents/LabelValueDisplay';
import addressToString from '../../../utils/addressToString';
import MaxWidthRestrictedContent from '../../v2BaseComponents/MaxWidthRestrictedContent';

const intl = getIntl('OrganizationPendingInvitation', {
    header: "Join {name}'s Primary Record Organization",
    subText:
        '{name} has invited you, {inviteeName}, as an {role} to join their Primary Record organization. Would you like to accept?',
    message: 'Message',
    decline: 'Decline',
    accept: 'Accept',
    organizationInvitation: 'Organization Invitation',
    name: 'Name',
    organizationDetails: 'Organization Details',
    email: 'Email',
    phoneNumber: 'Phone Number',
    faxNumber: 'Fax Number',
    url: 'URL',
    address: 'Address',
    role: 'Organization Role',
    messageFromInviter: 'Message from Inviter:',
});

export type OrganizationPendingInvitationOwnProps = {};

export type OrganizationPendingInvitationProps = OrganizationPendingInvitationOwnProps;

const OrganizationPendingInvitation: React.FC<OrganizationPendingInvitationProps> = () => {
    const resetApp = useResetApp();
    const {
        isLoadingMyOrganizationInvitation,
        respondToInvitation,
        myOrganizationInvitation,
        isRespondingToInvitation,
    } = useMyOrganizationInvitations({
        onAcceptInvitation: () => {
            resetApp();
        },
    });

    const getContent = (details: APITypes.organizationService.GetOrganizationUserInvitationMeResponse['items'][0]) => {
        return (
            <MaxWidthRestrictedContent>
                <Stack vertical fullWidth align="center" space={contentStackSpacing}>
                    <Text variant="h4">{intl('header', { name: details.organization.metadata.name })}</Text>
                    <Text variant="body-1">
                        {intl('subText', {
                            name: details.organization.metadata.name,
                            inviteeName: getName({
                                firstName: details.inviteeFirstName,
                                lastName: details.inviteeLastName,
                            }),
                            role: organizationRoles(details.inviteeRole),
                        })}
                    </Text>
                    {details.message && (
                        <Stack vertical fullWidth space={1} justify="flex-start">
                            <Text variant="caption-1">{intl('messageFromInviter')}</Text>
                            <Text variant="body-1">{details.message}</Text>
                        </Stack>
                    )}
                    <LabelValueDisplay
                        omitEmptyValues
                        headerTitle={intl('organizationDetails')}
                        items={[
                            { label: intl('name'), value: details.organization.metadata.name },
                            { label: intl('role'), value: organizationRoles(details.inviteeRole) },
                            {
                                label: intl('email'),
                                value: details.organization.contactDetails.email,
                                linkType: 'email',
                            },
                            {
                                label: intl('phoneNumber'),
                                value: details.organization.contactDetails.phoneNumber,
                                linkType: 'phone',
                            },
                            { label: intl('faxNumber'), value: details.organization.contactDetails.faxNumber },
                            { label: intl('url'), value: details.organization.contactDetails.url, linkType: 'url' },
                            {
                                label: intl('address'),
                                value: addressToString(details.organization.contactDetails),
                                linkType: 'address',
                            },
                        ]}
                    />
                </Stack>
            </MaxWidthRestrictedContent>
        );
    };

    return (
        <AnimateLoadedContent
            isLoading={isLoadingMyOrganizationInvitation || !myOrganizationInvitation || isRespondingToInvitation}
        >
            {() => (
                <ActionButtonLayout
                    scrollable
                    whiteBackground
                    actions={[
                        {
                            title: intl('decline'),
                            icon: 'XCircle',
                            variant: 'secondary',
                            testID: 'decline-organization-invitation',
                            onPress: () =>
                                respondToInvitation({
                                    organizationId: myOrganizationInvitation!.organizationId,
                                    invitationId: myOrganizationInvitation!.organizationUserInvitationId,
                                    response: 'reject',
                                }),
                        },
                        {
                            title: intl('accept'),
                            icon: 'CheckCircle',
                            testID: 'accept-organization-invitation',
                            onPress: () =>
                                respondToInvitation({
                                    organizationId: myOrganizationInvitation!.organizationId,
                                    invitationId: myOrganizationInvitation!.organizationUserInvitationId,
                                    response: 'accept',
                                }),
                        },
                    ]}
                >
                    <MainContentContainer>{getContent(myOrganizationInvitation!)}</MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(OrganizationPendingInvitation);
